import React from 'react';
import { Config } from '~/api/def/endpoints/dashboardEndpoint';
import { DynamicTableWidgetWrapper } from '~/components/ui/widgets/DynamicTableWidgetWrapper/DynamicTableWidgetWrapper';
import { DynamicHistogramWidgetWrapper } from '~/components/ui/widgets/DynamicHistogramWidgetWrapper';
import { DynamicPieChartWidgetWrapper } from '~/components/ui/widgets/DynamicPieChartWidgetWrapper';
import { mapBar } from '~/utils/mapBar';
import { findWidgetDefinition } from './widgetRegistry';

/**
 * Renders a React element for a widget configuration
 * @param config - Widget configuration object
 * @param mapBarFn - Function for mapping bar charts (optional, defaults to mapBar)
 * @returns React element for the widget or null if config doesn't match any widget type
 */
export const renderWidget = (
  config: Config,
  mapBarFn: typeof mapBar = mapBar,
): React.ReactElement | null => {
  // Handle dynamic widgets first (they have special logic)
  if (config.isDynamic) {
    if (config.renderComponent === 'dynamic_table' && config.query) {
      return (
        <DynamicTableWidgetWrapper
          widgetId={config.id}
          key={config.id}
          query={config.query}
          shortDescription={config.shortDescription}
        />
      );
    }

    if (config.renderComponent === 'dynamic_histogram' && config.query) {
      return (
        <DynamicHistogramWidgetWrapper
          widgetId={config.id}
          key={config.id}
          query={config.query}
          shortDescription={config.shortDescription}
          mapBar={mapBarFn}
        />
      );
    }

    if (config.renderComponent === 'dynamic_pie_chart' && config.query) {
      return (
        <DynamicPieChartWidgetWrapper
          widgetId={config.id}
          key={config.id}
          query={config.query}
          shortDescription={config.shortDescription}
          isAggregate={config.isAggregate}
          mapSlice={mapBarFn}
        />
      );
    }
  }

  // Find widget definition from registry
  const definition = findWidgetDefinition(config.id);
  if (definition) {
    return definition.renderFromConfig(config, mapBarFn);
  }

  return null;
};
